import type { RfxListItemResource } from "../../types/rfx-list.type";

/**
 * Loose shape consumed by the untyped legacy `.js` components
 * (`RFQCard`/`RFQGridView`/`RFQListView` — `checkJs: false`, so this is not
 * enforced at their call sites, but keeps this adapter itself honest about
 * exactly which fields it supplies). Deliberately NOT `Rfx` — every
 * T&Q/owner-only field (`supplierInvited`, `project`, `rfxType`,
 * `rfxPublished`, `supplierList`, `unreadMessages`, `description`,
 * `submissionTimezone`) is omitted, not faked; the legacy components render
 * those fields conditionally (see RFQCard.js's header comment for the exact
 * gates), so omission hides the corresponding UI instead of showing
 * blank/misleading data.
 */
export interface LegacyRfxRow {
	id: number;
	uuid: string;
	name: string | null;
	/** Mapped from `status_label` (string), never the raw numeric `status`. */
	status: string | undefined;
	submissionDeadline: string | undefined;
	/** FE-only — never sent by the server, read by the legacy click targets. */
	linkOverride: string;
}

/**
 * TASK-027 (second pass): maps a lean `RfxListItemResource` (Panel/Approvals)
 * onto `LegacyRfxRow`, without inventing or widening data.
 *
 * `status` is deliberately mapped from `status_label`, NOT the raw `status`
 * (a numeric backend code) — `StatusBadge`/`isRfxStatusMatch` call
 * `.toUpperCase()` on it, which throws on a number. `status_label` may not
 * match every legacy switch-case exactly, but degrades to the safe default
 * branch (renders the label as-is) instead of crashing.
 *
 * `linkOverride` is an FE-only field the legacy components read instead of
 * computing their own owner `viewRfx`/`editRfx` target — see
 * RFQCard.js/RFQListView.js's header comments.
 */
export const toLegacyRfxRow = (
	row: RfxListItemResource,
	linkOverride: string
): LegacyRfxRow => ({
	id: row.rfx_id,
	uuid: row.uuid,
	name: row.name,
	status: row.status_label ?? undefined,
	submissionDeadline: row.submission_deadline ?? undefined,
	linkOverride,
});
